import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, useLocation, type RouteObject } from "react-router-dom";
import { App } from "./App";
import { MakerGate } from "./components/MakerGate";
import { MonitoringPage } from "./pages/MonitoringPage";

/** Lazy pages: their code loads when the page is first opened, not with the shell. */
const RebalancingPage = lazy(() => import("./pages/RebalancingPage").then((m) => ({ default: m.RebalancingPage })));
const LimitsPage = lazy(() => import("./pages/LimitsPage").then((m) => ({ default: m.LimitsPage })));
const PairsPage = lazy(() => import("./pages/PairsPage").then((m) => ({ default: m.PairsPage })));
const TreasuryPage = lazy(() => import("./pages/TreasuryPage").then((m) => ({ default: m.TreasuryPage })));
const ExecutorPage = lazy(() => import("./pages/ExecutorPage").then((m) => ({ default: m.ExecutorPage })));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage").then((m) => ({ default: m.IntegrationsPage })));
const OrderHistoryPage = lazy(() => import("./pages/OrderHistoryPage").then((m) => ({ default: m.OrderHistoryPage })));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const DocsPage = lazy(() => import("./pages/DocsPage").then((m) => ({ default: m.DocsPage })));

function PageLoading() {
  return <div className="console-page" aria-busy="true" />;
}

const later = (page: ReactNode) => <Suspense fallback={<PageLoading />}>{page}</Suspense>;

/** The Quotes page is Pairs now: an old link to `/quotes` opens `/pairs`, its query and fragment kept. */
function QuotesMoved() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: "/pairs", search, hash }} replace />;
}

/**
 * One route per page of the sidebar, all behind the wallet gate. Sign-in is
 * not a route: the gate draws it in place of the page asked for, so the URL is
 * kept. There is no catch-all: an unknown path gets React Router's 404 screen.
 */
export const routes: RouteObject[] = [
  {
    path: "/",
    element: (
      <MakerGate>
        <App />
      </MakerGate>
    ),
    children: [
      { index: true, element: <MonitoringPage /> },
      { path: "rebalancing", element: later(<RebalancingPage />) },
      { path: "limits", element: later(<LimitsPage />) },
      { path: "pairs", element: later(<PairsPage />) },
      { path: "quotes", element: <QuotesMoved /> },
      { path: "treasury", element: later(<TreasuryPage />) },
      { path: "executor", element: later(<ExecutorPage />) },
      { path: "integrations", element: later(<IntegrationsPage />) },
      { path: "order-history", element: later(<OrderHistoryPage />) },
      { path: "notifications", element: later(<NotificationsPage />) },
      { path: "docs", element: later(<DocsPage />) },
    ],
  },
];

/** React Router v7 behaviour, opted into now (it is the default there). */
export const ROUTER_FUTURE = {
  v7_fetcherPersist: true,
  v7_normalizeFormMethod: true,
  v7_partialHydration: true,
  v7_relativeSplatPath: true,
  v7_skipActionErrorRevalidation: true,
} as const;

/** For `<RouterProvider future={PROVIDER_FUTURE}>`. */
export const PROVIDER_FUTURE = { v7_startTransition: true } as const;
